import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';

export interface DiscoverCountSkeletonProps {
  /** Optional class merged onto the pulse bar. */
  readonly className?: string;
  /** Test id. Defaults to `discover-count-skeleton`. */
  readonly 'data-testid'?: string;
  /**
   * Accessible status announced once per pending count cluster. Pass `null`
   * to suppress it when the CALLER already owns the pending announcement
   * (e.g. the results bar's own `aria-live` count line) — otherwise
   * assistive tech hears the same pending state twice.
   */
  readonly label?: string | null;
}

/**
 * Honest pending-count placeholder (WEB-786). A pulse bar is a loading
 * claim; a painted `0` is a measured claim. Use this until the shared
 * `/events/discover` envelope resolves so a later non-zero cannot refute
 * a first-paint zero.
 */
export function DiscoverCountSkeleton({
  className,
  'data-testid': dataTestId = 'discover-count-skeleton',
  label = 'Loading count',
}: DiscoverCountSkeletonProps): React.ReactElement {
  return (
    // A LABELLED skeleton is the announcing layer for its pending cluster, so
    // it carries `role="status"` — the loading state then reaches AT as a
    // status change rather than as text only a virtual cursor finds.
    //
    // An UNLABELLED one (`label={null}`) is pure decoration: the caller owns
    // the announcement (its own live region, or a sibling skeleton speaking
    // for the whole cluster). It gets no role at all — a bare `role="status"`
    // per repeated row would turn one pending list into N live regions.
    <span
      role={label ? 'status' : undefined}
      className="inline-flex items-center"
      data-testid={dataTestId}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block h-[1em] w-8 animate-pulse rounded bg-muted align-middle motion-reduce:animate-none',
          className,
        )}
      />
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}
